import type { BulkStorageResponse } from '@/storageAreas/api/models/storageAreaResponse';
import { defineStore, storeToRefs } from 'pinia';
import type { Product } from 'shared/products/domain/product';
import { useProductStore } from 'shared/products/stores/productStore';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { computed, ref } from 'vue';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import type { BulkProductContentModel } from '@/storageAreas/bulk/components/BulkContent.vue';
import { convertToDisplayUnit } from 'shared/common/domain/bulkUnits';

export interface EditBulkStorage {
  storageAreaId: string;
  bulkData: BulkStorageResponse;
  selectedStorageAreaIds: string[];
}

export interface CopyBulkStorage {
  bulkStorage: BulkStorageResponse;
}

export const useSelectedBulkStore = defineStore('selectedBulkStore', () => {
  const operation = ref<'create' | 'edit' | 'none'>('none');

  const productStore = useProductStore();
  const storageAreaStore = useStorageAreaStore();

  let storageAreaId: string | null = null;
  const selectedStorageAreaIds = ref<string[]>([]);
  const editMultiple = computed(() => selectedStorageAreaIds.value.length > 1);

  const model = ref<BulkStorageResponse>();

  const automaticProductChange = ref<boolean>(true);
  const automaticProductChangeInitial = computed(() => {
    if (!model.value) return;
    return !model.value.disableAutomaticProductChange;
  });
  const automaticProductChangeChanged = computed(
    () =>
      operation.value === 'edit' &&
      automaticProductChange.value !== automaticProductChangeInitial.value,
  );
  const automaticProductChangeEnabled = computed<boolean>(() => {
    if (!editMultiple.value) return true;

    const storageAreas = selectedStorageAreaIds.value
      .map((id) => storageAreaStore.getById(id))
      .filter((s) => s?.bulkStorageData !== null && s?.bulkStorageData !== undefined);
    if (storageAreas.length === 0) return false;

    const firstValue = !storageAreas[0]!.bulkStorageData!.disableAutomaticProductChange;
    return storageAreas.every(
      (sa) => !sa!.bulkStorageData!.disableAutomaticProductChange === firstValue,
    );
  });

  const allowMultipleProducts = ref<boolean>(false);
  const allowMultipleProductsInitial = computed(() => {
    if (!model.value) return;
    return model.value.allowMultipleProducts;
  });
  const allowMultipleProductsChanged = computed(
    () =>
      operation.value === 'edit' &&
      allowMultipleProducts.value !== allowMultipleProductsInitial.value,
  );
  const allowMultipleProductsEnabled = computed<boolean>(() => {
    if (!editMultiple.value) return true;

    const storageAreas = selectedStorageAreaIds.value
      .map((id) => storageAreaStore.getById(id))
      .filter((s) => s?.bulkStorageData !== null && s?.bulkStorageData !== undefined);
    if (storageAreas.length === 0) return false;

    const firstValue = storageAreas[0]!.bulkStorageData!.allowMultipleProducts;
    return storageAreas.every((sa) => sa!.bulkStorageData!.allowMultipleProducts === firstValue);
  });

  const allowedProducts = ref<Product[]>([]);
  const allowedProductsInitial = computed(() => {
    if (!model.value) return [];
    return model.value.allowedProducts;
  });
  const allowedProductsChanged = computed(() => {
    if (operation.value !== 'edit') return false;
    const newValue = new Set(allowedProducts.value.map((p) => p.id));
    const currentValue = new Set(allowedProductsInitial.value);

    if (currentValue.size !== newValue.size) return true;

    for (const v of currentValue) {
      if (!newValue.has(v)) {
        return true;
      }
    }

    for (const v of newValue) {
      if (!currentValue.has(v)) {
        return true;
      }
    }

    return false;
  });
  const allowedProductsEnabled = computed<boolean>(() => {
    if (!editMultiple.value) return true;

    const storageAreas = selectedStorageAreaIds.value
      .map((id) => storageAreaStore.getById(id))
      .filter((s) => s?.bulkStorageData !== null && s?.bulkStorageData !== undefined);
    if (storageAreas.length === 0) return false;

    const firstProducts = new Set(storageAreas[0]!.bulkStorageData!.allowedProducts);
    return storageAreas.every((sa) => {
      const products = new Set(sa!.bulkStorageData!.allowedProducts);
      if (products.size !== firstProducts.size) return false;
      for (const p of products) {
        if (!firstProducts.has(p)) return false;
      }
      return true;
    });
  });

  const maxWeight = ref<number | null>(0);
  const maxWeightInitial = computed(() => {
    if (!model.value) return;
    return model.value.maxWeight;
  });
  const maxWeightChanged = computed(() => {
    if (operation.value !== 'edit') return false;
    return maxWeight.value !== maxWeightInitial.value;
  });
    const maxWeightEnabled = computed<boolean>(() => {
      if (!editMultiple.value) return true;

      const storageAreas = selectedStorageAreaIds.value
        .map((id) => storageAreaStore.getById(id))
        .filter((s) => s?.bulkStorageData !== null && s?.bulkStorageData !== undefined);
      if (storageAreas.length === 0) return false;

      const firstValue = storageAreas[0]!.bulkStorageData!.maxWeight;
      return storageAreas.every((sa) => sa!.bulkStorageData!.maxWeight === firstValue);
    });

      const maxVolume = ref<number | null>(0);
      const maxVolumeInitial = computed(() => {
        if (!model.value) return [];
        return model.value.maxVolume;
      });
      const maxVolumeChanged = computed(() => {
        if (operation.value !== 'edit') return false;
        return maxVolume.value !== maxVolumeInitial.value;
      });
      const maxVolumeEnabled = computed<boolean>(() => {
        if (!editMultiple.value) return true;

        const storageAreas = selectedStorageAreaIds.value
          .map((id) => storageAreaStore.getById(id))
          .filter((s) => s?.bulkStorageData !== null && s?.bulkStorageData !== undefined);
        if (storageAreas.length === 0) return false;

        const firstValue = storageAreas[0]!.bulkStorageData!.maxVolume;
        return storageAreas.every((sa) => sa!.bulkStorageData!.maxVolume === firstValue);
      });

  const contents = computed(() => {
    const result: BulkProductContentModel[] = [];

    if (model.value) {
      for (const content of model.value.content) {
        const product = productStore.getById(content.productId);
        if (!product || !product.bulkData) continue;

        const displayUnit = product.bulkData.unit

        result.push({
          ton: content.ton,
          productId: product.id,
          productName: product.name,
          displayUnit: product.bulkData.unit,
          displayValue: convertToDisplayUnit(content.ton, displayUnit, product.bulkData.density)
        });
      }
    }
    return result;
  });

  const bulkStorageChanged = computed(() => {
    return (
      automaticProductChangeChanged.value ||
      allowMultipleProductsChanged.value ||
      allowedProductsChanged.value ||
      maxWeightChanged.value ||
      maxVolumeChanged.value
    );
  });

  storageAreaStore.changed.on((events) => {
    for (const ev of events) {
      if (ev.id !== storageAreaId) continue;
      if (ev.action === 'removed') continue;

      const bulkStorage = ev.data.bulkStorageData;
      if (bulkStorage) {
        fill(bulkStorage, false);
      }
      model.value = bulkStorage ?? undefined;
    }
  });

  function create() {
    storageAreaId = null;
    operation.value = 'create';
    model.value = undefined;
    selectedStorageAreaIds.value = [];

    automaticProductChange.value = true;
    allowMultipleProducts.value = false;
    allowedProducts.value = [];

    contents.value.length = 0;

    maxWeight.value = null;
    maxVolume.value = null;

  }

  function edit(action: EditBulkStorage) {
    selectedStorageAreaIds.value = action.selectedStorageAreaIds;
    const isFirstSelection = selectedStorageAreaIds.value.length === 1;

    if (isFirstSelection) {
      // First/single selection - initialize normally
      storageAreaId = action.storageAreaId;
      const bulkStorage = action.bulkData;
      fill(bulkStorage, true);
      model.value = bulkStorage;
    } else if (editMultiple.value) {
      // Multiple selections - update only matching fields
      const storageAreas = selectedStorageAreaIds.value
        .map((id) => storageAreaStore.getById(id))
        .filter((s) => s?.bulkStorageData !== null && s?.bulkStorageData !== undefined);

      if (automaticProductChangeEnabled.value) {
        const firstValue = !storageAreas[0]!.bulkStorageData!.disableAutomaticProductChange;
        if (
          storageAreas.every((sa) => !sa!.bulkStorageData!.disableAutomaticProductChange === firstValue)
        ) {
          automaticProductChange.value = firstValue;
        }
      }

      if (allowMultipleProductsEnabled.value) {
        const firstValue = storageAreas[0]!.bulkStorageData!.allowMultipleProducts;
        if (storageAreas.every((sa) => sa!.bulkStorageData!.allowMultipleProducts === firstValue)) {
          allowMultipleProducts.value = firstValue;
        }
      }

      if (allowedProductsEnabled.value) {
        const firstProducts = new Set(storageAreas[0]!.bulkStorageData!.allowedProducts);
        const allMatch = storageAreas.every((sa) => {
          const products = new Set(sa!.bulkStorageData!.allowedProducts);
          if (products.size !== firstProducts.size) return false;
          for (const p of products) {
            if (!firstProducts.has(p)) return false;
          }
          return true;
        });
        if (allMatch) {
          allowedProducts.value = [];
          for (const productId of storageAreas[0]!.bulkStorageData!.allowedProducts) {
            const product = productStore.getOrThrow(productId);
            allowedProducts.value.push(product);
          }
          allowedProducts.value.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { numeric: true }),
          );
        }
      }

        const firstMaxWeight = storageAreas[0]!.bulkStorageData!.maxWeight;
        if (storageAreas.every((sa) => sa!.bulkStorageData!.maxWeight === firstMaxWeight)) {
          maxWeight.value = firstMaxWeight;
        }

        const firstMaxVolume = storageAreas[0]!.bulkStorageData!.maxVolume;
        if (storageAreas.every((sa) => sa!.bulkStorageData!.maxVolume === firstMaxVolume)) {
          maxVolume.value = firstMaxVolume;
        }

    }

    operation.value = 'edit';
  }

  function copy(action: CopyBulkStorage) {
    storageAreaId = null;
    operation.value = 'create';
    selectedStorageAreaIds.value = [];

    const bulkStorage = action.bulkStorage;
    fill(bulkStorage, true);
    model.value = undefined;

    contents.value.length = 0;
  }

  function fill(bulkStorage: BulkStorageResponse, overwriteChanges: boolean) {
    if (overwriteChanges || !automaticProductChangeChanged.value) {
      automaticProductChange.value = !bulkStorage.disableAutomaticProductChange;
    }

    if (overwriteChanges || !allowMultipleProductsChanged.value) {
      allowMultipleProducts.value = bulkStorage.allowMultipleProducts;
    }

    if (overwriteChanges || !allowedProductsChanged.value) {
      allowedProducts.value = [];

      for (const productId of bulkStorage.allowedProducts) {
        const product = productStore.getOrThrow(productId);
        allowedProducts.value.push(product);
      }

      allowedProducts.value.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true }),
      );
    }

    if (overwriteChanges || !maxWeightChanged.value) {
      maxWeight.value = bulkStorage.maxWeight;
    }

    if (overwriteChanges || !maxVolumeChanged.value) {
      maxVolume.value = bulkStorage.maxVolume;
    }
  }

  function hide() {
    storageAreaId = null;
    operation.value = 'none';
    selectedStorageAreaIds.value = [];
    automaticProductChange.value = true;
    allowMultipleProducts.value = false;
    allowedProducts.value = [];
    maxWeight.value = null;
    maxVolume.value = null;
  }

  return {
    create,
    edit,
    copy,
    hide,
    operation,
    selectedStorageAreaIds,
    editMultiple,
    automaticProductChange,
    automaticProductChangeChanged,
    automaticProductChangeEnabled,
    allowMultipleProducts,
    allowMultipleProductsChanged,
    allowMultipleProductsEnabled,
    allowedProducts,
    allowedProductsChanged,
    allowedProductsEnabled,
    bulkStorageChanged,
    contents,
    maxWeight,
    maxWeightChanged,
    maxWeightEnabled,
    maxVolume,
    maxVolumeChanged,
    maxVolumeEnabled,
  };
});
